import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import Select from 'react-select';
import { SwatchesPicker } from 'react-color';
import DropdownMultiple from './DropdownMultiple';
import Dropdown from './Dropdown';
import { updateDepartmentSetting } from '../actions/apiDepartmentUpdates';

const EditDepartmentSettings = ({
  value: initialValue,
  checked: initialChecked,
  background_color,
  text_color,
  type,
  name,
  label,
  parentid,
  field,
  error,
  user_id,
  value: propValue,
}) => {
  const dispatch = useDispatch();
  const edit_department = useSelector((state) => state.settings_admin.edit_department);
  const vehicle_columns = useSelector((state) => state.settings_departments.vehicle_columns);
  const departments = useSelector((state) => state.settings_departments.original_departments);
  const related_installations = useSelector((state) => state.settings_dealerships.related_installations);
  const work_flows = useSelector((state) => state.settings_departments.work_flows);

  const [value, setValue] = useState(initialValue || '');
  const [checked, setChecked] = useState(initialChecked || false);

  const handleChangeComplete = useCallback(
    (e) => {
      const newValue = e.hex ? e.hex : 'transparent';
      setValue(newValue);
      const data = {
        id: parentid,
        [name]: newValue,
      };
      dispatch(updateDepartmentSetting(parentid, data, 'editColorPicker'));
    },
    [dispatch, parentid, name],
  );

  const onChangeInput = useCallback((e) => {
    setValue(e.target.value);
  }, []);

  const onBlur = useCallback(
    (e) => {
      if (e.target.value !== propValue) {
        const data = {
          id: parentid,
          [e.target.name]: e.target.value,
        };
        setValue(e.target.value);
        dispatch(updateDepartmentSetting(parentid, data, 'editUserProfileError'));
      }
    },
    [dispatch, parentid, propValue],
  );

  const onChangeSelect = useCallback(
    (e) => {
      const data = {
        id: parentid,
        [name]: e.value,
      };
      setValue(e.value);
      dispatch(updateDepartmentSetting(parentid, data, 'editVehicleColumnError'));
    },
    [dispatch, parentid, name],
  );

  const onRelatedInstallationsChangeSelect = useCallback(
    (e) => {
      const dealerArray = e.map((dealer) => dealer.value);
      const data = {
        id: parentid,
        [name]: dealerArray.join(','),
      };
      setValue(dealerArray);
      dispatch(updateDepartmentSetting(parentid, data, 'editVehicleColumnError'));
    },
    [dispatch, parentid, name],
  );

  const onChangeChecked = useCallback(
    (e) => {
      const data = {
        id: parentid,
        [e.target.name]: e.target.checked,
      };
      setChecked(e.target.checked);
      dispatch(updateDepartmentSetting(parentid, data, 'editVehicleColumnError'));
    },
    [dispatch, parentid],
  );

  const selectOptions = () => {
    // Assuming options prop is not used; adjust if needed
    return departments.map((option) => ({
      value: option.name || option,
      label: option.name || option,
    }));
  };

  const selectData = () => {
    return vehicle_columns.map((option) => ({
      value: option.name || option,
      label: option.name || option,
    }));
  };

  const selectDepartments = (excludeCurrent = false) => {
    const filteredDepartments = excludeCurrent
      ? departments.filter((d) => d.id !== edit_department.id)
      : departments;
    const returnDepartments = filteredDepartments.map((option) => ({
      id: parseInt(option.id),
      label: option.name,
      selected: option.id === parseInt(value),
    }));
    returnDepartments.push({ id: 0, label: 'None Selected' });
    return returnDepartments;
  };

  const selectWorkFlows = () => {
    const work_flows_values = (value || '').split(',');
    return work_flows.map((option) => ({
      id: option.id,
      label: option.name,
      selected: work_flows_values.includes(option.name),
    }));
  };

  const selectRelatedInstallations = () => {
    const dealer_values = (value || '').toString().split(',');
    return related_installations.map((option) => ({
      id: option.id,
      label: option.softname || option.subdomain,
      selected: dealer_values.includes(option.subdomain),
      dataid: 0,
      subdomain: option.subdomain,
    }));
  };

  const selectRelatedInstallationsSingle = () => {
    return related_installations.map((option) => ({
      id: option.id,
      label: option.softname || option.subdomain,
      selected: value == option.subdomain,
      dataid: 0,
      subdomain: option.subdomain,
    }));
  };

  const selectComparison = () => {
    const comparisonArray = [
      { name: 'Greater Than', label: 'Greater Than' },
      { name: 'Greater or Equal', label: 'Greater or Equal' },
      { name: 'Equals', label: 'Equals' },
      { name: 'Not Equals', label: 'Not Equals' },
      { name: 'Less Than', label: 'Less Than' },
      { name: 'Less or Equal', label: 'Less or Equal' },
      { name: 'Contains', label: 'Contains' },
    ];
    return comparisonArray.map((option) => ({
      value: option.name,
      label: option.label,
    }));
  };

  const selectDepartmentType = () => {
    const comparisonArray = [
      { id: 1, name: 'transfer', label: 'transfer' },
      { id: 2, name: 'return to origin', label: 'return to origin' },
      { id: 3, name: 'department', label: 'department' },
      { id: 4, name: 'data step', label: 'data step' },
      { id: 6, name: 'inventory', label: 'inventory' },
      { id: 7, name: 'recently sold', label: 'recently sold' },
      { id: 8, name: 'all reconditioning', label: 'all reconditioning' },
      { id: 9, name: 'all vehicles', label: 'all vehicles' },
      { id: 10, name: 'parent step', label: 'parent step' },
    ];
    return comparisonArray.map((option) => ({
      id: option.id,
      label: option.label,
      selected: option.label === value,
    }));
  };

  const returnDescription = () => {
    switch (name) {
      case 'active':
        return 'Step is Active';
      case 'goal':
        return 'Goal';
      case 'overdue_1':
        return '1st Notification';
      case 'overdue_2':
        return '2nd Notification';
      case 'overdue_3':
        return '3rd Notification';
      case 'children_departments':
        return 'Select children steps';
      case 'subgroup':
        return 'Parent step';
      case 'nonstep':
        return 'This stage is a representation of compared data. Example: "Jason", would be all vehicles that Jason appraised';
      case 'nonstep_meta':
        return 'This stage is a representation of compared data. Example: "Jason"';
      case 'nonstep_compare':
        return 'Comparison operator';
      case 'step':
        return 'Is this step considered part of the reconditioning process to be timed';
      case 'display_name':
        return 'Display Name';
      default:
        return '';
    }
  };

  const renderInput = () => {
    switch (type) {
      case 'switch':
      case 'switch-goal':
        return (
          <span className="switch">
            <input
              onChange={onChangeChecked}
              type="checkbox"
              name={name}
              data-id={parentid}
              key={`ds_${parentid}_${field}`}
              checked={checked}
              className={classnames('text-center', { 'form-control-danger': error })}
              id={`switch_id_${name}_${parentid}`}
            />
            <label className="text-capitalize" htmlFor={`switch_id_${name}_${parentid}`}>
              {label.replace(/_/g, ' ')}
            </label>
          </span>
        );
      case 'select':
        return (
          <div className="form-group">
            <label className="control-label">{label}</label>
            <Select
              onChange={onChangeSelect}
              value={selectOptions().filter(({ value: v }) => v === value)}
              name={name}
              options={selectOptions()}
              className="text-capitalize"
              data-id={parentid}
              clearable={false}
              searchable={false}
              key={`ds_${parentid}_${field}`}
              menuContainerStyle={{ zIndex: 5 }}
            />
          </div>
        );
      case 'select-data':
        return (
          <div className="form-group">
            <label className="control-label">{label}</label>
            <Select
              onChange={onChangeSelect}
              value={selectData().filter(({ value: v }) => v === value)}
              name={name}
              options={selectData()}
              className="text-capitalize"
              data-id={parentid}
              clearable={false}
              searchable
              key={`ds_${parentid}_${field}`}
              menuContainerStyle={{ zIndex: 5 }}
            />
          </div>
        );
      case 'select-data-raw':
        return (
          <Select
            onChange={onChangeSelect}
            value={selectData().filter(({ value: v }) => v === value)}
            name={name}
            options={selectData()}
            className="text-capitalize"
            data-id={parentid}
            clearable={false}
            searchable
            key={`ds_${parentid}_${field}`}
            menuContainerStyle={{ zIndex: 5 }}
          />
        );
      case 'select-comparison':
        return (
          <div className="form-group">
            <label className="control-label">{label}</label>
            <Select
              onChange={onChangeSelect}
              value={selectComparison().filter(({ value: v }) => v === value)}
              name={name}
              options={selectComparison()}
              className="text-capitalize"
              data-id={parentid}
              clearable={false}
              searchable={false}
              key={`ds_${parentid}_${field}`}
              menuContainerStyle={{ zIndex: 5 }}
            />
          </div>
        );
      case 'select-comparison-raw':
        return (
          <Select
            onChange={onChangeSelect}
            value={selectComparison().filter(({ value: v }) => v === value)}
            name={name}
            options={selectComparison()}
            className="text-capitalize"
            data-id={parentid}
            clearable={false}
            searchable={false}
            key={`ds_${parentid}_${field}`}
            menuContainerStyle={{ zIndex: 5 }}
          />
        );
      case 'select-department-type':
        return (
          <div className="col-md-12">
            <label className="control-label text-white">Set Department Type</label>
            <Dropdown
              titleHelper="Departments"
              title={value}
              list={selectDepartmentType()}
              update_type="department_type"
              user_id={user_id}
              department_id={parentid}
              value={value}
            />
          </div>
        );
      case 'select-departments':
        return (
          <div className="col-md-12">
            <label className="control-label text-white">Set Transfer Department</label>
            <Dropdown
              titleHelper="Departments"
              title="Select Department"
              list={selectDepartments()}
              update_type="subdomain_department"
              user_id={user_id}
              department_id={parentid}
              value={value}
            />
          </div>
        );
      case 'select-subgroup':
        return (
          <div className="col-md-12">
            <label className="control-label text-white">Set Parent Department</label>
            <Dropdown
              titleHelper="Departments"
              title="Select Department"
              list={selectDepartments(true)}
              update_type="subgroup"
              user_id={user_id}
              department_id={parentid}
              value={value}
            />
          </div>
        );
      case 'select-workflow':
        return (
          <div className="col-md-12">
            <label className="control-label text-white">Set Work Flows</label>
            <DropdownMultiple
              titleHelper="Work Flows"
              title="Select Work Flows"
              list={selectWorkFlows()}
              update_type="work_flows"
              user_id={user_id}
              department_id={parentid}
              set_work_flows={value}
            />
          </div>
        );
      case 'select-related-installations':
        return (
          <div className="col-md-12">
            <label className="control-label text-white">Transfer Vehicle To Location</label>
            <Dropdown
              titleHelper="Location"
              title="Select Location"
              list={selectRelatedInstallationsSingle()}
              update_type="subdomain"
              user_id={user_id}
              department_id={parentid}
              value={value}
            />
          </div>
        );
      case 'select-related-installations-multiple':
        return (
          <div className="col-md-12">
            <label className="control-label text-white">Locations</label>
            <DropdownMultiple
              titleHelper="Location"
              title="Select location"
              list={selectRelatedInstallations()}
              update_type="department_dealerships"
              user_id={user_id}
              department_id={parentid}
              dealers={value}
            />
          </div>
        );
      case 'text':
        return (
          <div className="form-group">
            <label className="control-label text-white">{label}</label>
            <input
              onChange={onChangeInput}
              onBlur={onBlur}
              value={value}
              type="text"
              name={name}
              data-id={parentid}
              key={`ds_${parentid}_${field}`}
              className={classnames('form-control', { 'form-control-danger': error })}
            />
          </div>
        );
      case 'order':
        return (
          <div className="form-group">
            <input
              onChange={onChangeInput}
              onBlur={onBlur}
              value={value}
              type="number"
              name={name}
              data-id={parentid}
              placeholder="Order"
              key={`ds_${parentid}_${field}`}
              className={classnames('form-control w30', { 'form-control-danger': error })}
            />
          </div>
        );
      case 'text-raw':
        return (
          <input
            onChange={onChangeInput}
            onBlur={onBlur}
            value={value}
            type="text"
            name={name}
            data-id={parentid}
            key={`ds_${parentid}_${field}`}
            className={classnames('form-control', { 'form-control-danger': error })}
          />
        );
      case 'text-goal':
        return (
          <div className="form-group">
            <input
              onChange={onChangeInput}
              onBlur={onBlur}
              value={value}
              type="number"
              name={name}
              data-id={parentid}
              placeholder="Enter Hours"
              key={`ds_${parentid}_${field}`}
              className={classnames('form-control', { 'form-control-danger': error })}
            />
          </div>
        );
      case 'colorpicker':
        return (
          <div className="form-group pad-right-1 space-right-1">
            <label className="control-label text-white">{label}</label>
            <SwatchesPicker
              color={value}
              onChangeComplete={handleChangeComplete}
              type={type}
              name={name}
              data-id={parentid}
              key={`ds_${parentid}_${field}`}
              background="transparent"
            />
            <button
              onClick={() => handleChangeComplete({ hex: '' })}
              style={{
                margin: '8px 15px',
                padding: '2px 18px',
                borderWidth: '3px',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Default
            </button>
          </div>
        );
      default:
        return (
          <input
            onChange={onChangeInput}
            onBlur={onBlur}
            value={value}
            type={type}
            name={name}
            className={classnames('form-control', { 'form-control-danger': error })}
          />
        );
    }
  };

  return (
    <div className="col-md-12">
      <div className={classnames('form-group', { 'has-danger': error })}>
        {renderInput()}
      </div>
      <div className="col-md-12">
        <small>
          <i className="fa fa-info-circle text-info" />
          {returnDescription()}
        </small>
      </div>
    </div>
  );
};

export default EditDepartmentSettings;